import type { DecadePalaceDto, LifeScaleDto, LuckDto } from '../../../contracts/cycles'
import { elementColor, palaceShort } from '../../colors'

/**
 * Жизненная шкала: две дорожки глав от рождения до девяноста — такты Ба Цзы и
 * дворцы Цзы Вэй. Прошлое притушено, «сейчас» отмечено вертикалью, тап по
 * сегменту выбирает главу для карточки под шкалой.
 *
 * SVG нарочно шире экрана и живёт в горизонтальной прокрутке: девяносто лет в
 * ширину телефона превращаются в нечитаемую гребёнку.
 */
export interface LifeScaleSelection {
  track: 'bazi' | 'ziwei'
  sequence: number
}

const MAX_AGE = 90
const PX_PER_YEAR = 14
const PAD = 18
const WIDTH = PAD * 2 + MAX_AGE * PX_PER_YEAR
const AXIS_Y = 18
const TRACK_TOP = 30
const TRACK_HEIGHT = 62
const TRACK_GAP = 12

export function baziEndAge(luck: LuckDto, next: LuckDto | undefined): number {
  if (next) return next.startAge
  return luck.startAge + Math.max(1, luck.endYear - luck.startYear + 1)
}

function x(age: number): number {
  return PAD + Math.max(0, Math.min(MAX_AGE, age)) * PX_PER_YEAR
}

export function LifeScaleChart({
  life,
  selected,
  onSelect,
}: {
  life: LifeScaleDto
  selected: LifeScaleSelection | null
  onSelect: (selection: LifeScaleSelection) => void
}) {
  const hasZiwei = life.ziwei.length > 0
  const ziweiTop = TRACK_TOP + TRACK_HEIGHT + TRACK_GAP
  const bottom = (hasZiwei ? ziweiTop + TRACK_HEIGHT : TRACK_TOP + TRACK_HEIGHT) + 22
  const nowX = x(life.age)

  return (
    <svg
      className="viz viz-life"
      viewBox={`0 0 ${WIDTH} ${bottom}`}
      width={WIDTH}
      height={bottom}
      role="img"
      aria-label="Жизненная шкала: такты и дворцы от рождения до девяноста лет"
    >
      {/* деления по десять лет */}
      {Array.from({ length: MAX_AGE / 10 + 1 }, (_, index) => index * 10).map((age) => (
        <g key={`tick-${age}`}>
          <line
            x1={x(age)}
            y1={AXIS_Y}
            x2={x(age)}
            y2={bottom - 18}
            style={{ stroke: 'var(--line)' }}
            strokeWidth={1.5}
          />
          <text x={x(age) + 4} y={AXIS_Y - 6} className="viz-cap">
            {age}
          </text>
        </g>
      ))}

      {/* такты Ба Цзы */}
      {life.bazi.map((luck, index) => {
        const endAge = baziEndAge(luck, life.bazi[index + 1])
        const isSelected = selected?.track === 'bazi' && selected.sequence === luck.sequence
        const color = elementColor(luck.pillar.element)
        return (
          <g
            key={`bazi-${luck.sequence}`}
            className="viz-life-seg"
            onClick={() => onSelect({ track: 'bazi', sequence: luck.sequence })}
          >
            <title>{`${luck.startYear}–${luck.endYear}: ${luck.pillar.stemRu} ${luck.pillar.branchRu}`}</title>
            <rect
              x={x(luck.startAge) + 1}
              y={TRACK_TOP}
              width={Math.max(6, x(endAge) - x(luck.startAge) - 2)}
              height={TRACK_HEIGHT}
              rx={10}
              style={{
                fill: color,
                fillOpacity: isSelected ? 0.34 : 0.16,
                stroke: isSelected ? 'var(--gold)' : color,
              }}
              strokeWidth={isSelected ? 2 : 1.5}
            />
            <text x={x(luck.startAge) + 11} y={TRACK_TOP + 22} className="viz-cap on">
              {luck.pillar.stemRu} {luck.pillar.branchRu}
            </text>
            <text x={x(luck.startAge) + 11} y={TRACK_TOP + 39} className="viz-cap">
              {luck.tenGod.nameRu}
            </text>
            <text x={x(luck.startAge) + 11} y={TRACK_TOP + 55} className="viz-cap dim">
              {luck.startYear}
            </text>
          </g>
        )
      })}

      {/* дворцы Цзы Вэй */}
      {life.ziwei.map((palace) => {
        const isSelected = selected?.track === 'ziwei' && selected.sequence === palace.sequence
        return (
          <g
            key={`ziwei-${palace.sequence}`}
            className="viz-life-seg"
            onClick={() => onSelect({ track: 'ziwei', sequence: palace.sequence })}
          >
            <title>{`${palace.startYear}–${palace.endYear}: ${palace.palaceRu}`}</title>
            <rect
              x={x(palace.startAge) + 1}
              y={ziweiTop}
              width={Math.max(6, x(palace.endAge + 1) - x(palace.startAge) - 2)}
              height={TRACK_HEIGHT}
              rx={10}
              style={{
                fill: 'var(--violet)',
                fillOpacity: isSelected ? 0.3 : 0.12,
                stroke: isSelected ? 'var(--gold)' : 'var(--violet)',
              }}
              strokeWidth={isSelected ? 2 : 1.5}
            />
            <text x={x(palace.startAge) + 11} y={ziweiTop + 22} className="viz-cap on">
              {palaceShort(palace.palaceId, palace.palaceRu)}
            </text>
            <text x={x(palace.startAge) + 11} y={ziweiTop + 39} className="viz-cap">
              {palace.stars[0]?.nameRu ?? ''}
            </text>
            <text x={x(palace.startAge) + 11} y={ziweiTop + 55} className="viz-cap dim">
              {palace.startAge} лет
            </text>
          </g>
        )
      })}

      {/* прошлое притушено */}
      {life.age > 0 && (
        <rect
          x={0}
          y={TRACK_TOP - 4}
          width={nowX}
          height={bottom - TRACK_TOP - 14}
          style={{ fill: 'var(--bg)', fillOpacity: 0.45, pointerEvents: 'none' }}
        />
      )}

      <line
        x1={nowX}
        y1={AXIS_Y}
        x2={nowX}
        y2={bottom - 18}
        style={{ stroke: 'var(--gold)', pointerEvents: 'none' }}
        strokeWidth={1.5}
      />
      <text x={nowX + 5} y={bottom - 5} className="viz-cap on" style={{ pointerEvents: 'none' }}>
        сейчас · {life.age}
      </text>
    </svg>
  )
}

export function currentBaziSequence(bazi: LuckDto[], age: number): number | null {
  const found = bazi.find((luck, index) => age >= luck.startAge && age < baziEndAge(luck, bazi[index + 1]))
  return found?.sequence ?? bazi[0]?.sequence ?? null
}

export function currentZiweiSequence(ziwei: DecadePalaceDto[], nominalAge: number): number | null {
  const found = ziwei.find((palace) => nominalAge >= palace.startAge && nominalAge <= palace.endAge)
  return found?.sequence ?? ziwei[0]?.sequence ?? null
}
